import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import { useNavigate } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";
import Spinners from "components/Common/Spinner";
import {
    fetchIncentiveMonthlyThreeTableAndFilterSchema,
    submitIncentiveMonthlyThreeFilterData,
    generateIncentiveMonthlyThree,
} from "model/IncentiveMonthlyModel";
import Loaders from "components/Common/Loaders";
import dayjs from "dayjs";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const getGenerateSwalMessage = (label) => {
    const currentMonthName = dayjs().format("MMMM");
    const currentYear = dayjs().format("YYYY");
    return `Generate ${label} For Month - ${currentMonthName}, ${currentYear}`;
};

const isSuccessResponse = (response) =>
    response?.success === true || response?.isSuccess === true || response?.status === true;

const MonthyListThree = () => {
    const { fetchMetadataSchema } = useCommonFilterOptions();
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("MonthyList");
    const initialFilterFetchedRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [monthlyListData, setMonthlyListData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const navigate = useNavigate();

    const defaultTableMetaData = [
        { Header: "ID", accessor: "id" },
        { Header: "Month", accessor: "month" },
        { Header: "Year", accessor: "year" },
        { Header: "Note", accessor: "note" },
        { Header: "Update", accessor: "update" },
    ];
    const getMonthlyTableSchema = async () => {
        const response = await fetchMetadataSchema("metadata:incentiveMonthlyThree", () =>
            fetchIncentiveMonthlyThreeTableAndFilterSchema({})
        );
        const columns = response?.data?.table?.columns || [];
        if (Array.isArray(columns) && columns.length) {
            setTableMetaData(
                columns.map((col) => ({
                    Header: col?.title,
                    accessor: col?.field,
                    type: col?.type,
                }))
            );
            return;
        }
        setTableMetaData(defaultTableMetaData);
    };

    const filteredData = useMemo(() => monthlyListData, [monthlyListData]);

    const getMonthlyList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = {
            ...requestPayload,
            ...payloadOverrides,
            status: "active",
        };
        const response = await submitIncentiveMonthlyThreeFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];
        const mappedData = (Array.isArray(listData) ? listData : []).map((item) => ({
            ...item,
            id: item?.id || item?._id || "",
            status: "active"
        }));

        if (response) {
            setMonthlyListData(mappedData || []);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(payload);
        } else {
            setMonthlyListData([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        getMonthlyTableSchema();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getMonthlyList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    const handleSearchSubmit = async (value) => {
        const trimmedValue = value?.trim() || "";
        await getMonthlyList({
            ...requestPayload,
            pageNumber: 1,
            nameContains: trimmedValue,
        });
    };

    const handleEdit = (row) => {
        navigate(`/${urlEndPoint.monthlyDetailsThree}/${row?.id}`);
    };

    const handlePageChange = async (page) => {
        await getMonthlyList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getMonthlyList({ pageNumber: 1, pageSize: rows });
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const swalResult = await Swal.fire({
                title: "Generate",
                html: getGenerateSwalMessage("Incentive"),
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Generate",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });
            if (!swalResult?.isConfirmed) return;

            Swal.fire({
                title: "Generating...",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const response = await generateIncentiveMonthlyThree();
            if (isSuccessResponse(response)) {
                Swal.close();
                toast.success(response?.message || "Generated successfully");
                await getMonthlyList({ pageNumber: 1 });
                return;
            }

            Swal.fire({
                icon: "error",
                title: "Unable to generate",
                text:
                    response?.error?.message ||
                    response?.message ||
                    "Unable to generate monthly incentive",
            });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Monthly List</h5>
                                <Button
                                    color="primary"
                                    onClick={handleGenerate}
                                    disabled={generating}
                                >
                                    Generate
                                </Button>
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setSearchQuery(nextValue);
                                                if (!nextValue.trim()) {
                                                    handleSearchSubmit("");
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />

                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={true}
                                    onEdit={handleEdit}
                                    isEditAccess={true}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    pageName={"MonthyList"}
                                />
                            ) : (
                                // <Card className="border mb-3">
                                //     <CardBody>
                                //         <Spinners setLoading={setLoading} />
                                //     </CardBody>
                                // </Card>
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

MonthyListThree.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(MonthyListThree);
